import { useCallback, useMemo, useState } from 'react';

import { useDebounce } from '@/shared/lib/hooks/use-debounce.hook';
import { useTanstackPagination } from '@/shared/lib/hooks/use-tanstack-pagination.hook';
import { SortChangePayload } from '@/shared/ui/table/table.types';

import type { TableState, UseTableStateOptions } from './table.types';
import { TableFeatures } from './table-schema';

export const useTableState = <TFilters,>(
	features: TableFeatures<TFilters>,
	options: UseTableStateOptions<TFilters> = {},
) => {
	const { filters } = options;
	const searchConfig = features.search;
	const sortingConfig = features.sorting;
	const paginationConfig = features.pagination;

	const searchEnabled = searchConfig?.enabled ?? false;
	const sortingEnabled = sortingConfig?.enabled ?? false;
	const paginationEnabled = paginationConfig?.enabled ?? false;

	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search, searchConfig?.debounceMs) as string;

	const [sort, setSort] = useState<SortChangePayload>({
		field: sortingConfig?.initial?.field ?? null,
		direction: sortingConfig?.initial?.direction ?? null,
	});

	const onChangeSort = useCallback((payload: SortChangePayload) => {
		setSort(payload);
	}, []);

	const filterParams = useMemo(() => {
		const mapper = features.filters?.mapToParams;

		if (mapper) {
			return mapper(filters);
		}

		return filters ?? {};
	}, [features.filters?.mapToParams, filters]);

	const resetKey = useMemo(
		() => ({
			...filterParams,
			...(searchEnabled && { search: debouncedSearch }),
			...(sortingEnabled && { sort: `${sort.field ?? ''}:${sort.direction ?? ''}` }),
		}),
		[filterParams, searchEnabled, debouncedSearch, sortingEnabled, sort],
	);

	const { pagination, pageSize, onChangePage, onChangePageSize } = useTanstackPagination({
		initialPage: paginationConfig?.initialPage,
		initialPageSize: paginationConfig?.initialPageSize,
		filters: resetKey,
	});

	const queryParams = useMemo(() => {
		const params: Record<string, unknown> = { ...filterParams };

		if (paginationEnabled) {
			params.page = pagination.page;
			params.size = pagination.size;
		}

		if (searchEnabled && debouncedSearch) {
			params.search = debouncedSearch;
		}

		if (sortingEnabled && sort.field && sort.direction) {
			params.sortBy = sort.field;
			params.orderBy = sort.direction;
		}

		return params;
	}, [
		filterParams,
		paginationEnabled,
		pagination.page,
		pagination.size,
		searchEnabled,
		debouncedSearch,
		sortingEnabled,
		sort.field,
		sort.direction,
	]);

	const state: TableState = useMemo(
		() => ({
			page: pagination.page,
			pageSize,
			onChangePage,
			onChangePageSize,
			sort,
			onChangeSort,
			search,
			setSearch,
			debouncedSearch,
		}),
		[pagination.page, pageSize, onChangePage, onChangePageSize, sort, onChangeSort, search, debouncedSearch],
	);

	return { state, queryParams };
};
